/**
 * API 에러 처리 모듈
 *
 * 모든 API 클라이언트에 통합 에러 처리를 제공:
 * - 에러 분류 (network, server, client, validation, parse, unknown)
 * - 재시도 가능한 에러 감지
 * - 사용자 친화적 에러 메시지
 */

import { decodeEnvelope } from "./response-envelope-codec";
import { translateCurrent } from "../i18n/current";

export enum ErrorCategory {
  NETWORK = "network",
  SERVER = "server",
  CLIENT = "client",
  VALIDATION = "validation",
  PARSE = "parse",
  UNKNOWN = "unknown",
}

/**
 * 재시도할 만한 일시적 에러를 나타내는 HTTP 상태 코드:
 * - 429: Too Many Requests (요청 제한)
 * - 502: Bad Gateway (upstream 서버 문제)
 * - 503: Service Unavailable (서버 과부하)
 * - 504: Gateway Timeout (upstream 서버 타임아웃)
 */
const RETRYABLE_STATUS_CODES = [429, 502, 503, 504];

export class APIError extends Error {
  public readonly name = "APIError";

  constructor(
    message: string,
    public readonly category: ErrorCategory,
    public readonly statusCode?: number,
    public readonly responseBody?: unknown,
    public readonly originalError?: Error,
    public readonly errorCode?: string,
    public readonly retryable?: boolean,
    public readonly retryAfterSeconds?: number
  ) {
    super(message);
    Object.setPrototypeOf(this, APIError.prototype);
  }

  isRetryable(): boolean {
    if (this.retryable !== undefined) {
      return this.retryable;
    }

    if (this.category === ErrorCategory.NETWORK) {
      return true;
    }

    if (this.statusCode && RETRYABLE_STATUS_CODES.includes(this.statusCode)) {
      return true;
    }

    return false;
  }

  getUserMessage(): string {
    switch (this.category) {
      case ErrorCategory.NETWORK:
        return translateCurrent("apiError.network");

      case ErrorCategory.SERVER:
        return translateCurrent("apiError.server");

      case ErrorCategory.CLIENT:
        return this.getClientErrorMessage();

      case ErrorCategory.VALIDATION:
        return translateCurrent("apiError.invalidData");

      case ErrorCategory.PARSE:
        return translateCurrent("apiError.parse");

      case ErrorCategory.UNKNOWN:
      default:
        return translateCurrent("apiError.unexpected");
    }
  }

  private getClientErrorMessage(): string {
    switch (this.statusCode) {
      case 401:
        return translateCurrent("apiError.sessionExpired");
      case 403:
        return translateCurrent("apiError.permissionDenied");
      case 404:
        return translateCurrent("apiError.notFound");
      case 429:
        return translateCurrent("apiError.tooManyRequests");
      default:
        return translateCurrent("apiError.requestInvalid");
    }
  }

  static fromNetworkError(error: Error, customMessage?: string): APIError {
    return new APIError(
      customMessage ?? error.message,
      ErrorCategory.NETWORK,
      undefined,
      undefined,
      error
    );
  }

  static fromHttpStatus(status: number, responseBody?: unknown): APIError {
    const category =
      status >= 500 ? ErrorCategory.SERVER : ErrorCategory.CLIENT;
    const fields = extractErrorFields(responseBody, status);

    return new APIError(
      fields.message,
      category,
      status,
      responseBody,
      undefined,
      fields.code,
      fields.retryable,
      fields.retryAfterSeconds
    );
  }

  static fromParseError(error: Error): APIError {
    return new APIError(
      `Failed to parse response: ${error.message}`,
      ErrorCategory.PARSE,
      undefined,
      undefined,
      error
    );
  }
}

export function isAuthServiceUnavailableError(error: unknown): boolean {
  return (
    error instanceof APIError &&
    error.statusCode === 503 &&
    error.errorCode === "auth_service_unavailable"
  );
}

/**
 * 오류 응답 body에서 APIError 를 채울 필드를 한 번의 디코딩으로 모두 뽑는다.
 *
 * quota 보호 엔드포인트는 정상 응답이 flat 이면서 pre-routing 429/503 만 envelope 로 오는
 * 혼합 계약이다. 소비자마다 message / code / retry 를 따로 디코딩하면 새 envelope 메타나
 * malformed 정책을 더할 때 소비자별로 다시 갈라지므로, 판정은 여기 한 곳에서 한다.
 */
export interface ErrorBodyFields {
  message: string;
  code?: string;
  retryable?: boolean;
  retryAfterSeconds?: number;
  /** envelope 를 표방했지만 계약을 어긴 경우의 사유. 정상 flat 오류에는 없다. */
  violation?: string;
}

export function extractErrorFields(
  responseBody: unknown,
  status: number
): ErrorBodyFields {
  const decoded = decodeEnvelope(responseBody);

  if (decoded.kind === "error") {
    return {
      message: decoded.detail.message,
      code: decoded.detail.code,
      retryable: decoded.detail.retryable,
      retryAfterSeconds: decoded.detail.retry_after_seconds,
    };
  }

  if (decoded.kind === "malformed") {
    // 계약을 어긴 본문을 flat 로 흘리면 error 객체가 다시 문자열화된다.
    return {
      message: `Server returned a malformed response (HTTP ${status}).`,
      violation: decoded.violation,
    };
  }

  return {
    message: flatErrorMessage(responseBody, status),
    code: flatErrorCode(responseBody),
  };
}

function flatErrorMessage(responseBody: unknown, status: number): string {
  if (responseBody && typeof responseBody === "object") {
    const body = responseBody as Record<string, unknown>;

    if (typeof body.error === "string") {
      return body.error;
    }
    if (typeof body.message === "string") {
      return body.message;
    }
    if (typeof body.detail === "string") {
      return body.detail;
    }
  }

  return `Request failed with status ${status}`;
}

function flatErrorCode(responseBody: unknown): string | undefined {
  if (responseBody && typeof responseBody === "object") {
    const body = responseBody as Record<string, unknown>;
    if (typeof body.error_code === "string") {
      return body.error_code;
    }
    if (typeof body.code === "string") {
      return body.code;
    }
  }

  return undefined;
}

/** 응답 body에서 에러 메시지 추출 */
export function extractErrorMessage(
  responseBody: unknown,
  status: number
): string {
  return extractErrorFields(responseBody, status).message;
}

export function extractErrorCode(responseBody: unknown): string | undefined {
  return extractErrorFields(responseBody, 0).code;
}
